/**
 * Tab Query Sync Mixin
 *
 * Synchronizes component tabs with URL query parameters for better navigation
 * and bookmark support.
 *
 * Usage:
 * 1. Import the mixin: import tabQuerySync from '@/mixins/tabQuerySync'
 * 2. Add to component mixins: mixins: [tabQuerySync]
 * 3. Configure in data():
 *    - Set tabQueryConfig with queryParam name and valid tab values
 *    - Set activeTab to use activeTabIndex from mixin
 *
 * Example:
 * ```js
 * export default {
 *   mixins: [tabQuerySync],
 *   data() {
 *     return {
 *       tabQueryConfig: {
 *         queryParam: 'tab',  // URL query param name
 *         validValues: ['all', 'active', 'inactive'],  // Valid tab identifiers
 *         defaultValue: 'all',  // Default if no/invalid query param
 *       },
 *       tabs: [
 *         { id: 'all', label: 'All' },
 *         { id: 'active', label: 'Active' },
 *         { id: 'inactive', label: 'Inactive' },
 *       ],
 *     }
 *   },
 *   computed: {
 *     activeTab: {
 *       get() {
 *         return this.activeTabIndex
 *       },
 *       set(index) {
 *         this.setActiveTab(index)
 *       },
 *     },
 *   },
 * }
 * ```
 */
export default {
  data() {
    return {
      // Override this in your component
      tabQueryConfig: {
        queryParam: 'tab',
        validValues: [],
        defaultValue: null,
        additionalValidParams: [], // Other query params to preserve
        disabledWhen: null, // Function returning true to disable tab sync (e.g., () => this.showProfile)
      },
    }
  },

  computed: {
    /**
     * Get current tab value from query parameter
     * Falls back to default if query param is invalid
     */
    currentTabValue() {
      const { queryParam, validValues, defaultValue } = this.tabQueryConfig
      const queryValue = this.$route.query[queryParam]

      return validValues.includes(queryValue) ? queryValue : defaultValue
    },

    /**
     * Convert tab value to index for v-tabs
     */
    activeTabIndex() {
      const { validValues } = this.tabQueryConfig
      const index = validValues.indexOf(this.currentTabValue)
      return index >= 0 ? index : 0
    },

    /**
     * Get current tab object from tabs array
     */
    currentTabFromQuery() {
      if (!this.tabs) return null
      const index = this.activeTabIndex
      return this.tabs[index] || this.tabs[0]
    },
  },

  watch: {
    /**
     * Watch for external route changes (back/forward navigation)
     * and update component state accordingly
     */
    '$route.query': {
      handler(newQuery, oldQuery) {
        const { queryParam } = this.tabQueryConfig
        if (newQuery[queryParam] !== oldQuery?.[queryParam]) {
          // Query changed externally (browser back/forward)
          // Component will reactively update via activeTabIndex
          this.$emit('tab-changed-from-route', this.currentTabValue)
        }
      },
      deep: true,
    },
  },

  methods: {
    /**
     * Update the active tab and sync to URL query parameter
     * @param {Number} index - Tab index to activate
     */
    setActiveTab(index) {
      const { queryParam, validValues } = this.tabQueryConfig
      const value = validValues[index]

      if (!value) {
        // Invalid tab index, do nothing
        return
      }

      // Clean and update query
      const cleanedQuery = this.cleanTabQueryParameters(this.$route.query)
      cleanedQuery[queryParam] = value

      // Avoid unnecessary navigation if already on this tab
      if (this.$route.query[queryParam] === value) {
        return
      }

      this.$router.push({ query: cleanedQuery }).catch((err) => {
        // Ignore navigation duplicated errors
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },

    /**
     * Clean query parameters, keeping only valid ones
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object
     */
    cleanTabQueryParameters(currentQuery) {
      const { queryParam, additionalValidParams = [] } = this.tabQueryConfig
      const validParameters = [queryParam, ...additionalValidParams]
      const cleanedQuery = {}

      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    /**
     * Initialize tab from query parameter on mount
     */
    initializeTabFromQuery() {
      const { queryParam, defaultValue, disabledWhen } = this.tabQueryConfig

      // Skip if tab sync is disabled (e.g., on profile pages)
      if (typeof disabledWhen === 'function' && disabledWhen()) {
        return
      }

      const currentValue = this.$route.query[queryParam]

      // If no query param, set default
      if (!currentValue && defaultValue) {
        // Preserve all existing query params and add the default tab value
        const newQuery = {
          ...this.$route.query,
          [queryParam]: defaultValue,
        }

        this.$router.replace({ query: newQuery }).catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },
  },

  mounted() {
    // Initialize tab from query on mount
    this.initializeTabFromQuery()
  },
}
